import { resetPointCloudFetchFailures } from '@/lib/pointcloud/rangeCacheBypass';
import { isCustomDomain } from './domain';

/**
 * Base URL for the signed-cookie-protected resources CDN.
 * On custom domains resources are proxied same-origin under /resources/* (the
 * multi-tenant CloudFront distribution strips the prefix) so the signed
 * cookies are first-party. Everywhere else it's the resources distribution.
 */
export const getResourcesEndpoint = (): string =>
  isCustomDomain() ? `${window.location.origin}/resources` : import.meta.env.VITE_RESOURCES_ENDPOINT;

const RESOURCES_ORIGIN = new URL(import.meta.env.VITE_RESOURCES_ENDPOINT).origin;

/**
 * Server responses contain absolute resources URLs (libs/common resourceUrl()).
 * On custom domains rewrite them to the same-origin /resources form. Applied
 * once at the GraphQL execute layer so every consumer gets rewritten URLs.
 */
export const rewriteResourceUrls = <T>(data: T): T => {
  if (!isCustomDomain()) return data;
  const rewrite = (value: unknown): unknown => {
    if (typeof value === 'string') {
      return value.startsWith(`${RESOURCES_ORIGIN}/`)
        ? `${window.location.origin}/resources/${value.slice(RESOURCES_ORIGIN.length + 1)}`
        : value;
    }
    if (Array.isArray(value)) return value.map(rewrite);
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, rewrite(v)]));
    }
    return value;
  };
  return rewrite(data) as T;
};

/** Set CloudFront signed cookies first-party on the current (custom) origin. */
export const applyCfCookies = (cookies: Record<string, string>): void => {
  for (const [name, value] of Object.entries(cookies)) {
    document.cookie = `${name}=${value}; Path=/; Secure; SameSite=Lax`;
  }
  // Fresh access cookies: point-cloud reads refused before (403s held back by the
  // fetch backoff) may succeed now, so forget those failures.
  resetPointCloudFetchFailures();
};
